import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import StepProgressIndicator from '../../components/ui/StepProgressIndicator';
import WorkflowBreadcrumb from '../../components/ui/WorkflowBreadcrumb';
import NavigationActions from '../../components/ui/NavigationActions';
import MapView from './components/MapView';
import LocationCard from './components/LocationCard';
import FilterControls from './components/FilterControls';
import ParticipantsSummary from './components/ParticipantsSummary';
import ResultsStats from './components/ResultsStats';
import Icon from '../../components/AppIcon';
import { trackLocationSelected, trackTravelTime } from '../../utils/analytics';
import { Loader } from '@googlemaps/js-api-loader';

const MeetingPointResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const participantsFromState = location?.state?.participants || [];
  const meetingIdFromState = location?.state?.meetingId;
  const sessionCodeFromState = location?.state?.sessionCode;
  const selectedTypesFromState = location?.state?.selectedTypes || ['cafe', 'restaurant'];
  const radiusFromState = location?.state?.radius || 5;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [meetingPoints, setMeetingPoints] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [sortBy, setSortBy] = useState('fairness');
  const [viewMode, setViewMode] = useState('split');
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const timeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Validate participants data on mount
  useEffect(() => {
    console.log('[Process] Checking for participant data...');
    if (!participantsFromState || participantsFromState?.length === 0) {
      console.error('[Process] ERROR: No participant data found. Redirecting to location-input.');
      navigate('/location-input', { 
        replace: true,
        state: { error: 'No participant data found. Please enter locations to continue.' }
      });
    }
  }, [participantsFromState, navigate]);

  // Load Google Maps API once with timeout
  const loadGoogleMaps = async () => {
    const apiKey = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey || apiKey === 'your-google-maps-api-key-here') {
      throw new Error('Google Maps API key not configured. Please add VITE_GOOGLE_MAPS_API_KEY to your .env file.');
    }

    console.log('[GoogleMaps] Loading Google Maps API...');
    
    // Check if already loaded
    if (window.google?.maps?.places?.PlacesService && window.google?.maps?.Geocoder) {
      console.log('[GoogleMaps] Google Maps API already loaded');
      setGoogleMapsLoaded(true);
      return window.google;
    }

    try {
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        timeoutRef.current = setTimeout(() => {
          reject(new Error('Google Maps API loading timeout. Please check your internet connection and try again.'));
        }, 15000); // 15 second timeout
      });

      // Create loader promise
      const loaderPromise = (async () => {
        const loader = new Loader({
          apiKey: apiKey,
          version: 'weekly',
          libraries: ['places', 'geometry']
        });
        await loader?.load();
        return window.google;
      })();

      // Race between loader and timeout
      let google = await Promise.race([loaderPromise, timeoutPromise]);
      
      // Clear timeout if successful
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
        timeoutRef.current = null;
      }

      console.log('[GoogleMaps] Google Maps API loaded successfully');
      setGoogleMapsLoaded(true);
      return google;
    } catch (error) {
      // Clear timeout on error
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
        timeoutRef.current = null;
      }
      console.error('[GoogleMaps] Failed to load Google Maps API:', error);
      throw new Error(`Failed to load Google Maps API: ${error.message}`);
    }
  };

  // Calculate geographic midpoint from coordinates
  const calculateMidpoint = (coordinates) => {
    if (!coordinates || coordinates?.length === 0) return null;

    // Convert to radians and calculate average
    let x = 0, y = 0, z = 0;

    coordinates?.forEach(coord => {
      const lat = coord?.lat * Math.PI / 180;
      const lng = coord?.lng * Math.PI / 180;

      x += Math.cos(lat) * Math.cos(lng);
      y += Math.cos(lat) * Math.sin(lng);
      z += Math.sin(lat);
    });

    const total = coordinates?.length;
    x = x / total;
    y = y / total;
    z = z / total;

    const centralLng = Math.atan2(y, x);
    const centralSquareRoot = Math.sqrt(x * x + y * y);
    const centralLat = Math.atan2(z, centralSquareRoot);

    return {
      lat: centralLat * 180 / Math.PI,
      lng: centralLng * 180 / Math.PI
    };
  };

  // Geocode address to coordinates
  const geocodeAddress = async (address, google) => {
    console.log(`[Geocode] Starting geocode for address: "${address}"`);
    
    try {
      const geocoder = new google.maps.Geocoder();
      
      // Create timeout for geocoding
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Geocoding timeout for address: ${address}`));
        }, 10000); // 10 second timeout per address
      });

      const geocodePromise = new Promise((resolve, reject) => {
        geocoder.geocode({ address: address }, (results, status) => {
          console.log(`[Geocode] Status for "${address}": ${status}`);
          
          if (status === 'OK' && results?.[0]) {
            const location = results[0].geometry.location;
            const coords = { lat: location.lat(), lng: location.lng() };
            console.log(`[Geocode] SUCCESS for "${address}":`, coords);
            resolve(coords);
          } else if (status === 'ZERO_RESULTS') {
            console.error(`[Geocode] ZERO_RESULTS for "${address}"`);
            reject(new Error(`Address not found: ${address}`));
          } else if (status === 'OVER_QUERY_LIMIT') {
            console.error(`[Geocode] OVER_QUERY_LIMIT for "${address}"`);
            reject(new Error('Google Maps API quota exceeded. Please try again later.'));
          } else {
            console.error(`[Geocode] FAILED for "${address}": ${status}`);
            reject(new Error(`Failed to geocode address: ${address} (Status: ${status})`));
          }
        });
      });

      return await Promise.race([geocodePromise, timeoutPromise]);
    } catch (error) {
      console.error(`[Geocode] EXCEPTION for "${address}":`, error);
      throw error;
    }
  };

  // Find nearby places using Google Places API
  const findNearbyPlaces = async (midpoint, google, selectedTypesFromState, radiusFromState) => {
    console.log('[Places] Starting nearby search at midpoint:', midpoint);
    
    try {
      const service = new google.maps.places.PlacesService(document.createElement('div'));
      const location = new google.maps.LatLng(midpoint.lat, midpoint.lng);
      
      // Search for multiple types of venues
      const types = ['cafe', 'restaurant', 'bar', 'park'];
      const radius = 5000; // 5km radius

      console.log(`[Places] Searching with radius: ${radius}m, types:`, types);

      // Create timeout for places search - CRITICAL: This must reject to trigger fallback
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          console.error('[Places] TIMEOUT: Places API did not respond within 8 seconds');
          reject(new Error('PLACES_TIMEOUT'));
        }, 8000); // 8 second timeout
      });

      const searchPromise = new Promise((resolve, reject) => {
        try {
          service.nearbySearch(
            {
              location: location,
              radius: radius,
              // Don't specify type to get all nearby places
            },
            (results, status) => {
              console.log(`[Places] Search callback triggered with status: ${status}`);
              console.log(`[Places] Results count: ${results?.length || 0}`);
              
              if (status === google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
                let places = results.slice(0, 10).map((place, index) => {
                  const placeData = {
                    id: index + 1,
                    name: place.name,
                    address: place.vicinity,
                    type: place.types?.[0]?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'Venue',
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                    rating: place.rating || 4.0,
                    image: place.photos?.[0] 
                      ? place.photos[0].getUrl({ maxWidth: 400 })
                      : 'https://images.unsplash.com/photo-1731346701448-bbd8d0251e8f',
                    imageAlt: `${place.name} - ${place.types?.[0]?.replace('_', ' ') || 'venue'}`,
                    travelTimes: []
                  };
                  console.log(`[Places] Place ${index + 1}:`, placeData.name, placeData.type);
                  return placeData;
                });
                console.log(`[Places] SUCCESS: Found ${places.length} places`);
                resolve(places);
              } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                console.warn('[Places] ZERO_RESULTS: No places found near midpoint');
                reject(new Error('ZERO_RESULTS'));
              } else if (status === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
                console.error('[Places] OVER_QUERY_LIMIT: API quota exceeded');
                reject(new Error('OVER_QUERY_LIMIT'));
              } else {
                console.error(`[Places] FAILED: Status ${status}`);
                reject(new Error(`PLACES_ERROR_${status}`));
              }
            }
          );
        } catch (err) {
          console.error('[Places] Exception in nearbySearch call:', err);
          reject(err);
        }
      });

      return await Promise.race([searchPromise, timeoutPromise]);
    } catch (error) {
      console.error('[Places] EXCEPTION:', error);
      throw error;
    }
  };

  // Generate fallback mock places near midpoint
  const generateFallbackPlaces = (midpoint, count = 8) => {
    console.log('[Fallback] Generating mock places near midpoint:', midpoint);
    
    const placeTypes = ['Cafe', 'Restaurant', 'Park', 'Coffee Shop', 'Bar', 'Library'];
    const placeNames = [
      'Central Meeting Point',
      'Midway Cafe',
      'Halfway House Restaurant',
      'Junction Coffee',
      'Crossroads Park',
      'Meeting Place Bistro',
      'Rendezvous Cafe',
      'Midpoint Lounge'
    ];
    
    return Array.from({ length: count }, (_, index) => {
      // Generate locations in a circle around midpoint (within ~2km)
      const angle = (index / count) * 2 * Math.PI;
      const distance = 0.01 + Math.random() * 0.01; // ~1-2km radius
      const lat = midpoint?.lat + distance * Math.cos(angle);
      const lng = midpoint?.lng + distance * Math.sin(angle);
      
      return {
        id: index + 1,
        name: placeNames?.[index] || `Meeting Point ${index + 1}`,
        address: `Near midpoint location`,
        type: placeTypes?.[index % placeTypes?.length],
        lat: lat,
        lng: lng,
        rating: 3.5 + Math.random() * 1.5, // 3.5-5.0 rating
        image: 'https://images.unsplash.com/photo-1731346701448-bbd8d0251e8f',
        imageAlt: `${placeNames?.[index] || `Meeting Point ${index + 1}`} - meeting location`,
        travelTimes: []
      };
    });
  };

  // Calculate travel time estimate based on distance and transport
  const estimateTravelTime = (fromCoord, toCoord, transport) => {
    // Calculate distance using Haversine formula
    const R = 6371; // Earth's radius in km
    const dLat = (toCoord?.lat - fromCoord?.lat) * Math.PI / 180;
    const dLng = (toCoord?.lng - fromCoord?.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(fromCoord?.lat * Math.PI / 180) * Math.cos(toCoord?.lat * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in km

    // Estimate time based on transport mode (in minutes)
    const speeds = {
      'driving': 50, // km/h average in city
      'transit': 30,
      'walking': 5,
      'bicycling': 15
    };

    const speed = speeds?.[transport] || 30;
    return Math.round((distance / speed) * 60); // Convert to minutes
  };

  // Load and process participant data
  useEffect(() => {
    const processParticipants = async () => {
      try {
        console.log('\n=== MEETING POINT CALCULATION START ===');
        setLoading(true);
        setError(null);

        console.log('[Process] Participants from state:', participantsFromState);
        
        if (!participantsFromState || participantsFromState?.length < 2) {
          const error = 'No participant data found. Please go back and enter locations.';
          console.error('[Process] ERROR:', error);
          setError(error);
          setLoading(false);
          return;
        }

        // Step 0: Load Google Maps API
        console.log('[Process] Step 0: Loading Google Maps API...');
        let google;
        try {
          google = await loadGoogleMaps();
          console.log('[Process] Google Maps API loaded successfully');
        } catch (err) {
          console.error('[Process] Failed to load Google Maps API:', err);
          setError(err?.message || 'Failed to load Google Maps API. Please check your API key configuration.');
          setLoading(false);
          return;
        }

        console.log('[Process] Step 1: Geocoding', participantsFromState?.length, 'participant addresses...');
        
        // Geocode all participant locations with individual error handling
        const geocodedParticipants = await Promise.all(
          participantsFromState?.map(async (p, index) => {
            try {
              console.log(`[Process] Geocoding participant ${index + 1}:`, p?.location);
              const coords = await geocodeAddress(p?.location, google);
              const participant = {
                id: p?.id || index + 1,
                name: `Participant ${index + 1}`,
                location: p?.location,
                transport: p?.transport,
                lat: coords?.lat,
                lng: coords?.lng
              };
              console.log(`[Process] Participant ${index + 1} geocoded successfully:`, participant);
              return participant;
            } catch (err) {
              console.error(`[Process] Failed to geocode participant ${index + 1} (${p?.location}):`, err);
              return null;
            }
          })
        );

        const validParticipants = geocodedParticipants?.filter(p => p !== null);
        console.log('[Process] Valid participants after geocoding:', validParticipants?.length, '/', participantsFromState?.length);

        if (validParticipants?.length < 2) {
          const error = 'Could not geocode participant locations. Please check addresses and try again.';
          console.error('[Process] ERROR:', error);
          setError(error);
          setLoading(false);
          return;
        }

        setParticipants(validParticipants);
        console.log('[Process] Step 2: Calculating geographic midpoint...');

        // Calculate midpoint
        const midpoint = calculateMidpoint(validParticipants?.map(p => ({ lat: p?.lat, lng: p?.lng })));
        console.log('[Process] Midpoint calculated:', midpoint);

        if (!midpoint) {
          const error = 'Could not calculate midpoint.';
          console.error('[Process] ERROR:', error);
          setError(error);
          setLoading(false);
          return;
        }

        console.log('[Process] Step 3: Finding nearby places...');
        
        // Find nearby places with fallback
        let places = [];
        try {
          places = await findNearbyPlaces(midpoint, google, selectedTypesFromState, radiusFromState);
          console.log('[Process] Places found:', places?.length);
        } catch (err) {
          console.error('[Process] Places search error:', err);
          
          // Check if it's a timeout or API failure - use fallback
          if (err?.message === 'PLACES_TIMEOUT' || err?.message === 'ZERO_RESULTS' || err?.message?.includes('PLACES_ERROR')) {
            console.warn('[Process] Using fallback mock places due to Places API issue');
            places = generateFallbackPlaces(midpoint, 8);
            console.log('[Process] Generated', places?.length, 'fallback places');
          } else {
            // For other errors (quota, network), show error
            setError(err?.message || 'Failed to find nearby places. Please try again.');
            setLoading(false);
            return;
          }
        }

        if (places?.length === 0) {
          console.warn('[Process] No places found, generating fallback locations');
          places = generateFallbackPlaces(midpoint, 8);
        }

        console.log('[Process] Step 4: Calculating travel times for each location...');
        
        // Calculate travel times for each place
        const placesWithTravelTimes = places?.map(place => {
          const travelTimes = validParticipants?.map(p => {
            const duration = estimateTravelTime(
              { lat: p?.lat, lng: p?.lng },
              { lat: place?.lat, lng: place?.lng },
              p?.transport
            );
            console.log(`[Process] Travel time from ${p?.name} to ${place?.name}: ${duration} min`);
            return {
              participantId: p?.id,
              duration
            };
          });
          
          return {
            ...place,
            travelTimes
          };
        });

        console.log('[Process] Final meeting points with travel times:', placesWithTravelTimes?.length);
        setMeetingPoints(placesWithTravelTimes);
        console.log('=== MEETING POINT CALCULATION COMPLETE ===\n');
        setLoading(false);
      } catch (err) {
        console.error('\n=== MEETING POINT CALCULATION FAILED ===');
        console.error('[Process] EXCEPTION:', err);
        const errorMessage = err?.message || 'Failed to calculate meeting points. Please try again.';
        setError(errorMessage);
        setLoading(false);
      }
    };

    processParticipants();

    // Cleanup function
    return () => {
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
        timeoutRef.current = null;
      }
      if (abortControllerRef?.current) {
        abortControllerRef?.current?.abort();
        abortControllerRef.current = null;
      }
    };
  }, [participantsFromState]);

  const sortMeetingPoints = (points) => {
    const sorted = [...points];

    switch (sortBy) {
      case 'fairness':
        return sorted?.sort((a, b) => {
          const aMax = Math.max(...a?.travelTimes?.map((t) => t?.duration));
          const aMin = Math.min(...a?.travelTimes?.map((t) => t?.duration));
          const bMax = Math.max(...b?.travelTimes?.map((t) => t?.duration));
          const bMin = Math.min(...b?.travelTimes?.map((t) => t?.duration));
          return aMax - aMin - (bMax - bMin);
        });
      case 'average':
        return sorted?.sort((a, b) => {
          const aAvg = a?.travelTimes?.reduce((sum, t) => sum + t?.duration, 0) / a?.travelTimes?.length;
          const bAvg = b?.travelTimes?.reduce((sum, t) => sum + t?.duration, 0) / b?.travelTimes?.length;
          return aAvg - bAvg;
        });
      case 'max':
        return sorted?.sort((a, b) => {
          const aMax = Math.max(...a?.travelTimes?.map((t) => t?.duration));
          const bMax = Math.max(...b?.travelTimes?.map((t) => t?.duration));
          return aMax - bMax;
        });
      case 'rating':
        return sorted?.sort((a, b) => (b?.rating || 0) - (a?.rating || 0));
      default:
        return sorted;
    }
  };

  const sortedMeetingPoints = sortMeetingPoints(meetingPoints);

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    
    // Track location selection
    trackLocationSelected({
      name: location?.name,
      type: location?.type,
      rating: location?.rating,
      phone: location?.phone
    });
  };

  const handleConfirmLocation = async () => {
    if (!selectedLocation) return;

    try {
      setLoading(true);

      // Track travel times for analytics
      participants?.forEach(participant => {
        if (participant?.travelTime) {
          trackTravelTime({
            travelTime: participant?.travelTime,
            distance: participant?.distance,
            transportMode: 'driving'
          });
        }
      });

      // Create meeting with selected location
    } catch (error) {
      console.error('Error confirming location:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (selectedLocation) {
      navigate('/location-type-filter', {
        state: {
          meetingId: meetingIdFromState,
          sessionCode: sessionCodeFromState,
          selectedLocation,
          participants
        }
      });
    }
  };

  const handleSelectLocation = (location) => {
    setSelectedLocation(location);
    
    // Track location selection
    trackLocationSelected(location?.name, location?.type);
    
    // Track travel times for analytics
    location?.travelTimes?.forEach(tt => {
      const participant = participants?.find(p => p?.id === tt?.participantId);
      if (participant) {
        trackTravelTime(participant?.name, tt?.duration, participant?.transport);
      }
    });
    
    // Navigate to filter page with meeting ID in URL
    navigate(`/location-type-filter?meetingId=${meetingIdFromState}`, {
      state: {
        selectedLocation: location,
        participants: participants,
        meetingId: meetingIdFromState,
        sessionCode: sessionCodeFromState
      }
    });
  };

  // Show loading state
  if (loading) {
    return (
      <>
        <Helmet>
          <title>Calculating Meeting Points - Mosey</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <StepProgressIndicator />
          <WorkflowBreadcrumb />
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <div className="text-center space-y-2">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground">
                  Calculating Optimal Meeting Points
                </h2>
                <p className="text-sm md:text-base text-muted-foreground max-w-md">
                  Analyzing {participantsFromState?.length} locations and finding the fairest meeting spots...
                </p>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <>
        <Helmet>
          <title>Error - Mosey</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <StepProgressIndicator />
          <WorkflowBreadcrumb />
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
                <Icon name="AlertCircle" size={32} className="text-destructive" />
              </div>
              <div className="text-center space-y-3 max-w-md">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground">
                  Unable to Calculate Meeting Points
                </h2>
                <p className="text-sm md:text-base text-muted-foreground">
                  {error}
                </p>
                <button
                  onClick={() => navigate(-1)}
                  className="mt-4 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
                >
                  Go Back and Try Again
                </button>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Meeting Point Results - Mosey</title>
        <meta name="description" content="View optimal meeting locations with equal travel times for all participants. Compare options and select the best meeting point." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <StepProgressIndicator />
        <WorkflowBreadcrumb />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 lg:py-10 pb-32">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Target" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
                  Meeting Point Results
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  We found {sortedMeetingPoints?.length} optimal locations with fair travel times
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6 md:space-y-8">
            <ResultsStats
              meetingPoints={sortedMeetingPoints}
              participants={participants} />
            

            <ParticipantsSummary participants={participants} />

            <FilterControls
              sortBy={sortBy}
              onSortChange={setSortBy}
              viewMode={viewMode}
              onViewModeChange={setViewMode} />
            

            {viewMode === 'split' &&
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                <div className="order-2 lg:order-1">
                  <div className="sticky top-24 h-[400px] md:h-[500px] lg:h-[600px]">
                    <MapView
                    participants={participants}
                    meetingPoints={sortedMeetingPoints}
                    selectedLocation={selectedLocation}
                    onLocationSelect={handleLocationSelect} />
                  
                  </div>
                </div>

                <div className="order-1 lg:order-2 space-y-4 md:space-y-6">
                  {sortedMeetingPoints?.map((location) =>
                <LocationCard
                  key={location?.id}
                  location={location}
                  participants={participants}
                  isSelected={selectedLocation === location?.id}
                  onSelect={handleLocationSelect} />

                )}
                </div>
              </div>
            }

            {viewMode === 'map' &&
            <div className="h-[500px] md:h-[600px] lg:h-[700px]">
                <MapView
                participants={participants}
                meetingPoints={sortedMeetingPoints}
                selectedLocation={selectedLocation}
                onLocationSelect={handleLocationSelect} />
              
              </div>
            }

            {viewMode === 'list' &&
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {sortedMeetingPoints?.map((location) =>
              <LocationCard
                key={location?.id}
                location={location}
                participants={participants}
                isSelected={selectedLocation === location?.id}
                onSelect={handleLocationSelect} />

              )}
              </div>
            }
          </div>
        </main>

        <NavigationActions
          onNext={handleNext}
          onPrevious={() => navigate(-1)}
          isNextDisabled={!selectedLocation}
          nextLabel="Refine Location" />
        
      </div>
    </>);

};

export default MeetingPointResults;